import React, { createContext, useState, useContext } from 'react';
import { firestore, timestamp } from '../firebase/config';
import { collection, addDoc, updateDoc, doc, getDoc, getDocs, query, where, deleteDoc } from 'firebase/firestore';

// Création du contexte des voyages
const TripContext = createContext();

// Hook personnalisé pour utiliser le contexte des voyages
export const useTrip = () => {
  return useContext(TripContext);
};

// Fournisseur du contexte des voyages
export const TripProvider = ({ children }) => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fonction pour récupérer tous les voyages
  const getTrips = async () => {
    try {
      setLoading(true);
      const tripsCollection = collection(firestore, 'trips');
      const tripsSnapshot = await getDocs(tripsCollection);
      const tripsList = tripsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTrips(tripsList);
      setLoading(false);
      return tripsList;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Fonction pour récupérer un voyage par ID
  const getTripById = async (id) => {
    try {
      const tripDoc = await getDoc(doc(firestore, 'trips', id));
      if (tripDoc.exists()) {
        return { id: tripDoc.id, ...tripDoc.data() };
      } else {
        return null;
      }
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour créer un nouveau voyage
  const createTrip = async (tripData) => {
    try {
      const { departureTime, arrivalTime, ...rest } = tripData;
      
      const newTrip = {
        ...rest,
        departureTime: timestamp(departureTime),
        arrivalTime: timestamp(arrivalTime),
        status: 'scheduled',
        createdAt: timestamp(),
        updatedAt: timestamp()
      };
      
      const docRef = await addDoc(collection(firestore, 'trips'), newTrip);
      return { id: docRef.id, ...newTrip };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour mettre à jour un voyage
  const updateTrip = async (id, tripData) => {
    try {
      const tripRef = doc(firestore, 'trips', id);
      
      // Préparer les données à mettre à jour
      const updatedData = { ...tripData, updatedAt: timestamp() };
      
      // Convertir les dates en timestamps pour Firestore
      if (tripData.departureTime) {
        const departureTime = tripData.departureTime;
        updatedData.departureTime = timestamp(departureTime);
      }
      
      if (tripData.arrivalTime) {
        const arrivalTime = tripData.arrivalTime;
        updatedData.arrivalTime = timestamp(arrivalTime);
      }
      
      await updateDoc(tripRef, updatedData);
      return { id, ...updatedData };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour annuler un voyage
  const cancelTrip = async (id, reason) => {
    try {
      const tripRef = doc(firestore, 'trips', id);
      await updateDoc(tripRef, {
        status: 'cancelled',
        cancellationReason: reason,
        cancelledAt: timestamp(),
        updatedAt: timestamp()
      });
      return { id, status: 'cancelled', cancellationReason: reason };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour marquer un voyage comme terminé
  const completeTrip = async (id) => {
    try {
      const tripRef = doc(firestore, 'trips', id);
      await updateDoc(tripRef, {
        status: 'completed',
        completedAt: timestamp(),
        updatedAt: timestamp()
      });
      return { id, status: 'completed' };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Valeur du contexte
  const value = {
    trips,
    loading,
    error,
    getTrips,
    getTripById,
    createTrip,
    updateTrip,
    cancelTrip,
    completeTrip
  };

  return (
    <TripContext.Provider value={value}>
      {children}
    </TripContext.Provider>
  );
};

export { TripContext };
